import React from 'react';
import { ContextMenuState } from 'src/types/ViewModel/Vessel/TankPlan';
import { canUndoStowage, undoStowageForCargoOperation } from 'src/utils/stowage/stowageOperation/undoStowageUtils';
import { RotationCargoOperation } from 'src/types/ViewModel/Stowage/Rotation/RotationCargoOperation';
import { useStowageStore } from 'src/context/StowageContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { hasPermission } from 'src/utils/stowage/permissions/stowagePermissionsUtils';
import { useAuthStore } from 'src/context/AuthContext';
import { canEditCargoTemperature } from 'src/utils/stowage/stowageOperation/volumeUtils';
import { stowageFeatureEnum } from 'src/enums/stowage/stowageFeatureEnum';
import usePostStowageApi from 'src/api/stowage/usePostStowageApi';
import { createDeleteStowagePlanChange } from 'src/factory/Stowage/StowagePlanChanges/StowagePlanChangeFactory';
import { StowageChangeDataType } from 'src/types/ViewModel/Stowage/Enums/StowageChangeDataType';
import { RotationCargo } from 'src/types/ViewModel/Stowage/Rotation/RotationCargo';
import CargoNumber from '../../common/cargoNumber';
import { UUID } from 'crypto';
import { getCargoOperationWeight, isLoadingOrUnloadingCargoOperation } from 'src/utils/stowage/cargoOperationUtils';
import OdfjellContextMenu from 'src/components/ui/odfjellContextMenu/OdfjellContextMenu';
import { StowagePlanChange } from 'src/types/ViewModel/Stowage/StowagePlanChange';
import { getBerthCalls } from 'src/utils/stowage/stowageRotationUtils';
import { canMergeCargo, getMergeableCargo, getCargoMergeDescription, mergeCargoes } from 'src/utils/stowage/stowageOperation/mergeCargoUtils';

interface MenuItem {
    isSeparator?: boolean;
    content: React.ReactNode;
    isDisabled: boolean;
    onClick?: () => void;
    className?: string;
}

interface OperationListContextMenuProps {
    contextMenuState: ContextMenuState;
    selectedCargoOperation: RotationCargoOperation;
}

const OperationListContextMenu: React.FC<OperationListContextMenuProps> = ({ contextMenuState, selectedCargoOperation }) => {
    const {
        activeStowagePlan,
        stowagePlans,
        setOperationListContextMenuState,
        setIsAdjustCargoTemperaturePanelOpen,
        setIsShowOperationForCargoPanelOpen,
        setIsCargoOperationPanelOpen,
        setIsCargoColorPanelOpen,
        setIsSplitCargoOperationPanelOpen,
        setStowageModalData,
    } = useStowageStore();
    const { saveStowageData } = usePostStowageApi();
    const { role } = useAuthStore();

    const getCommodityBookLink = () => (
        <a
            href={'https://commoditybook.odfjell.com/#/product/' + selectedCargoOperation.cargo.productId}
            target="_blank"
            title="Click here to open the Commodity Book"
            rel="noreferrer"
        >
            Open the Commodity Book...
            <FontAwesomeIcon className="external-link-icon" icon="external-link-alt" />
        </a>
    );

    const closeContextMenu = () => {
        setOperationListContextMenuState({ visible: false, cargoContents: [] } as ContextMenuState);
    };

    const handleOperation = async (operation: (() => Promise<void>) | (() => void)) => {
        if (!stowagePlans || !activeStowagePlan || !selectedCargoOperation) {
            closeContextMenu();
            return;
        }
        try {
            const result = operation();
            if (result instanceof Promise) {
                await result;
            }
            closeContextMenu();
        } catch (error) {
            console.error('Error handling operation:', error);
        }
    };

    const onUndoStowage = async () => {
        const _stowagePlan = { ...activeStowagePlan };
        const stowagePlanChanges = undoStowageForCargoOperation(_stowagePlan, selectedCargoOperation);
        await saveStowageData(_stowagePlan, stowagePlanChanges);
    };

    const onMergeCargo = () => {
        const mergeCandidate = getMergeableCargo(activeStowagePlan, selectedCargoOperation.cargo);
        if (!mergeCandidate) return;

        const description = getCargoMergeDescription(selectedCargoOperation.cargo, mergeCandidate);

        setStowageModalData({
            title: 'Merge cargo',
            message: (
                <>
                    Are you sure you want to merge this cargo with its corresponding IMOS cargo?
                    <br />
                    <br />
                    The manual cargo will be removed and all associated cargo operations will be reassigned to the IMOS cargo.
                </>
            ),
            action: async () => {
                const _stowagePlan = { ...activeStowagePlan };
                const stowagePlanChanges = mergeCargoes(_stowagePlan, selectedCargoOperation.cargo);
                await saveStowageData(_stowagePlan, stowagePlanChanges);
                setStowageModalData(null);
            },
            cancel: () => setStowageModalData(null),
            actionButtonText: 'Merge',
        });
    };

    const onEditCargoTemperature = () => {
        setIsAdjustCargoTemperaturePanelOpen(true);
    };

    const onEditCargoOperation = () => {
        setIsCargoOperationPanelOpen({ cargoOperation: selectedCargoOperation, berthCall: selectedCargoOperation.berthCall, visible: true });
    };

    const onNewCargoOperation = () => {
        setIsCargoOperationPanelOpen({ cargoOperation: null, berthCall: selectedCargoOperation.berthCall, visible: true });
    };

    const onSelectNewCargoColor = () => {
        setIsCargoColorPanelOpen(true);
    };

    const onSplitCargoOperation = () => {
        setIsSplitCargoOperationPanelOpen(true);
    };

    const canDeleteOperation = () => {
        if (!activeStowagePlan || !selectedCargoOperation || selectedCargoOperation.operationType === 'Transit') {
            return false;
        }

        if (selectedCargoOperation.operationType === 'Unloading' || selectedCargoOperation.operationType === 'Transfer') {
            return true;
        }

        const unloadingOperations = activeStowagePlan.cargoOperations.filter(
            (op) => op.cargoGuid === selectedCargoOperation.cargoGuid && op.operationType === 'Unloading'
        );
        if (unloadingOperations.length > 0) {
            return false;
        }
        return true;
    };

    const canDeleteCargo = () => {
        if (!activeStowagePlan || !selectedCargoOperation) {
            return false;
        }

        return true;
    };

    const canEditCargoOperation = () => {
        return isLoadingOrUnloadingCargoOperation(selectedCargoOperation);
    };

    const canSplitCargoOperation = () => {
        return (
            (selectedCargoOperation.operationType === 'Loading' &&
                activeStowagePlan.stowageItems.filter((item) => item.cargoOperation.cargoGuid === selectedCargoOperation.cargoGuid).length === 0) ||
            (selectedCargoOperation.operationType === 'Unloading' &&
                activeStowagePlan.stowageItems.filter((item) => item.cargoOperationGuid === selectedCargoOperation.cargoOperationGuid).length === 0)
        );
    };

    const canAddUnloadingCargoOperation = () => {
        const loadingContractWeight = getCargoOperationWeight(activeStowagePlan, selectedCargoOperation.cargo, 'Loading', 'contract');
        const unloadingContractWeight = getCargoOperationWeight(activeStowagePlan, selectedCargoOperation.cargo, 'Unloading', 'contract');

        return (
            selectedCargoOperation.operationType === 'Transit' &&
            loadingContractWeight > unloadingContractWeight &&
            getBerthCalls(selectedCargoOperation.berthCall.portCall).length > 0
        );
    };

    const handleStowageChanges = async (changes: StowagePlanChange[]) => {
        await saveStowageData(activeStowagePlan, changes);
        setStowageModalData(null);
    };

    const createDeleteChanges = (guid: UUID, type: StowageChangeDataType) =>
        createDeleteStowagePlanChange(activeStowagePlan.stowagePlanGuid, guid, type);

    const createDeleteConfirmationModal = (title: string, message: React.ReactNode, action: () => void) => {
        setStowageModalData({
            title,
            message,
            action,
            cancel: () => setStowageModalData(null),
            actionButtonText: 'Delete',
        });
    };

    const onDeleteOperation = () => {
        createDeleteConfirmationModal(
            'Delete operation',
            <>
                Are you sure you want to delete this cargo operation for cargo{' '}
                <CargoNumber includeColoredBackground cargo={selectedCargoOperation.cargo} />?
            </>,
            () => deleteOperation(selectedCargoOperation)
        );
    };

    const deleteOperation = async (cargoOperation: RotationCargoOperation) => {
        const stowageItemsToDelete = activeStowagePlan.stowageItems.filter((item) => item.cargoOperationGuid === cargoOperation.cargoOperationGuid);

        const changes = [
            createDeleteChanges(cargoOperation.cargoOperationGuid, StowageChangeDataType.RotationCargoOperation),
            ...stowageItemsToDelete.map((item) => createDeleteChanges(item.stowageGuid, StowageChangeDataType.StowageItem)),
        ];

        await handleStowageChanges(changes);
    };

    const onDeleteCargo = (cargo: RotationCargo) => {
        createDeleteConfirmationModal(
            'Delete cargo',
            <>
                Are you sure you want to delete the cargo <CargoNumber includeColoredBackground cargo={cargo} />?
                <br />
                <br />
                This action will remove all operations and associated stowages linked to this cargo from this stowage plan.
            </>,
            () => deleteCargo(cargo)
        );
    };

    const deleteCargo = async (cargo: RotationCargo) => {
        const itemsToDelete = {
            operations: activeStowagePlan.cargoOperations.filter((op) => op.cargoGuid === cargo.cargoGuid),
            stowageItems: activeStowagePlan.stowageItems.filter((item) => item.cargoOperation.cargoGuid === cargo.cargoGuid),
            temperatures: activeStowagePlan.adjustedCargoTemperatures.filter((temp) => temp.cargoGuid === cargo.cargoGuid),
        };

        const changes = [
            createDeleteChanges(cargo.cargoGuid, StowageChangeDataType.RotationCargo),
            ...itemsToDelete.operations.map((op) => createDeleteChanges(op.cargoOperationGuid, StowageChangeDataType.RotationCargoOperation)),
            ...itemsToDelete.stowageItems.map((item) => createDeleteChanges(item.stowageGuid, StowageChangeDataType.StowageItem)),
            ...itemsToDelete.temperatures.map((temp) =>
                createDeleteChanges(temp.adjustedCargoTemperatureGuid, StowageChangeDataType.AdjustedCargoTemperature)
            ),
        ];

        await handleStowageChanges(changes);
    };

    const onShowOperationForCargo = (cargoOperation: RotationCargoOperation) => {
        if (!activeStowagePlan || !cargoOperation) {
            return;
        }

        setIsShowOperationForCargoPanelOpen(true);
    }; // TODO: ERROR-HANDLING - Implement the logic for showing violations for cargo
    // const onShowViolationsForCargo = (cargoOperation: RotationCargoOperation) => {
    //     if (!activeStowagePlan || !cargoOperation) {
    //         return;
    //     }

    //     setIsShowOperationForCargoPanelOpen(true);
    // };

    const menuItemConfigs = [
        {
            content: 'Undo stowage',
            permission: stowageFeatureEnum.EditStowage,
            isEnabled: () => canUndoStowage(activeStowagePlan, selectedCargoOperation),
            action: () => onUndoStowage(),
            className: '',
        },
        { isSeparator: true },
        {
            content: 'Add new cargo operation',
            permission: stowageFeatureEnum.EditCargo,
            isEnabled: () => canEditCargoOperation(),
            action: () => onNewCargoOperation(),
        },
        {
            content: 'Edit cargo operation',
            permission: stowageFeatureEnum.EditCargo,
            isEnabled: () => canEditCargoOperation(),
            action: () => onEditCargoOperation(),
        },
        {
            content: 'Split cargo operation',
            permission: stowageFeatureEnum.EditCargo,
            isEnabled: () => canSplitCargoOperation(),
            action: () => onSplitCargoOperation(),
        },
        {
            content: 'Add unloading operation',
            permission: stowageFeatureEnum.EditCargo,
            isEnabled: () => canAddUnloadingCargoOperation(),
            action: () => onSplitCargoOperation(),
        },
        {
            content: 'Delete cargo operation',
            permission: stowageFeatureEnum.EditCargo,
            isEnabled: () => canDeleteOperation(),
            action: () => onDeleteOperation(),
        },
        {
            content: 'Merge cargo',
            permission: stowageFeatureEnum.EditCargo,
            isEnabled: () => canMergeCargo(activeStowagePlan, selectedCargoOperation?.cargo),
            action: () => onMergeCargo(),
            hideWhenDisabled: true,
        },
        { isSeparator: true },
        {
            content: 'Show operations for cargo',
            permission: stowageFeatureEnum.ViewStowagePlan,
            isEnabled: () => selectedCargoOperation,
            action: () => onShowOperationForCargo(selectedCargoOperation),
        },
        // TODO: ERROR-HANDLING - Implement the logic for showing violations for cargo
        // {
        //     content: 'Show violations for cargo',
        //     permission: stowageFeatureEnum.EditCargo,
        //     isEnabled: () => selectedCargoOperation,
        //     action: () => onShowViolationsForCargo(selectedCargoOperation),
        // },
        {
            content: 'Adjust cargo temperature',
            permission: stowageFeatureEnum.AdjustCargoTemperature,
            isEnabled: () => canEditCargoTemperature(selectedCargoOperation),
            action: () => onEditCargoTemperature(),
        },
        {
            content: 'Select new cargo color',
            permission: stowageFeatureEnum.EditCargo,
            isEnabled: () => !!selectedCargoOperation,
            action: () => onSelectNewCargoColor(),
        },
        {
            content: 'Delete cargo',
            permission: stowageFeatureEnum.EditStowage,
            isEnabled: () => canDeleteCargo(),
            action: () => onDeleteCargo(selectedCargoOperation.cargo),
        },
        { content: getCommodityBookLink(), permission: stowageFeatureEnum.ViewStowagePlan, isEnabled: () => true },
    ];

    const createMenuItems = (): MenuItem[] =>
        menuItemConfigs
            .filter((config) => {
                // Hide items that have hideWhenDisabled=true and are disabled
                if (config.hideWhenDisabled) {
                    const isEnabled = hasPermission(role, config.permission) && config.isEnabled?.();
                    return isEnabled;
                }
                return true;
            })
            .map((config) => ({
                isSeparator: config.isSeparator ?? false,
                content: config.content,
                isDisabled: !(hasPermission(role, config.permission) && config.isEnabled?.()),
                onClick: config.action ? async () => handleOperation(config.action) : undefined,
                className: config.className,
            }));

    return (
        <>
            {menuItemConfigs.length > 0 && (
                <OdfjellContextMenu
                    position={{ x: contextMenuState.x, y: contextMenuState.y }}
                    menuItems={createMenuItems()}
                    dataContextMenu="operation-list"
                    className="operation-list-context-menu"
                />
            )}
        </>
    );
};

export default React.memo(OperationListContextMenu);
